/* istanbul ignore file */
import type { ApiRequestOptions } from '../core/ApiRequestOptions';
import type { ApiResult } from '../core/ApiResult';
import type { OnCancel } from '../core/CancelablePromise';
import type { OpenAPIConfig } from '../core/OpenAPI';
import { CancelablePromise } from '../core/CancelablePromise';
import { ApiError } from '../core/ApiError';
import { OpenAPI } from '../core/OpenAPI';
import { getToken } from '@bettercomp/ams-spa-auth';
import { getAuthCookie } from 'utils/auth';
import { logout } from 'service/cognitoService';
import { fetchWithRenewal } from 'service/amsFetch';
import { isAmsAuthActive } from 'service/amsSession';
import { captureApiError } from 'utils/supportContext/errorBuffer';

// TODO: remove after migration to codegen:mp based services
OpenAPI.BASE = process.env.NEXT_PUBLIC_API_ENDPOINT || '/api';

const BLOB_CONTENT_TYPES = [
    'text/csv; charset=utf-8',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const isDefined = <T>(value: T | null | undefined): value is Exclude<T, null | undefined> => {
    return value !== undefined && value !== null;
};

const isString = (value: unknown): value is string => {
    return typeof value === 'string';
};

const isStringWithValue = (value: unknown): value is string => {
    return isString(value) && value !== '';
};

const isBlob = (value: unknown): value is Blob => {
    return (
        value !== null &&
        typeof (value as Blob) === 'object' &&
        typeof (value as Blob).type === 'string' &&
        typeof (value as Blob).stream === 'function' &&
        typeof (value as Blob).arrayBuffer === 'function' &&
        typeof (value as Blob).constructor === 'function' &&
        typeof (value as Blob).constructor.name === 'string' &&
        /^(Blob|File)$/.test(value?.constructor.name || '')
    );
};

const isFormData = (value: unknown): value is FormData => {
    return value instanceof FormData;
};

const base64 = (str: string): string => {
    try {
        return btoa(str);
    } catch {
        return Buffer.from(str).toString('base64');
    }
};

const getQueryString = (params: Record<string, unknown>): string => {
    const qs: string[] = [];

    const append = (key: string, value: unknown) => {
        qs.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    };

    const process = (key: string, value: unknown) => {
        if (isDefined(value)) {
            if (typeof value === 'object' && value) {
                Object.entries(value).forEach(([k, v]) => {
                    process(`${key}[${k}]`, v);
                });
            } else {
                append(key, value);
            }
        }
    };

    Object.entries(params).forEach(([key, value]) => {
        process(key, value);
    });

    if (qs.length > 0) {
        return `?${qs.join('&')}`;
    }

    return '';
};

const getUrl = (config: OpenAPIConfig, options: ApiRequestOptions): string => {
    const encoder = config.ENCODE_PATH || encodeURI;

    const path = options.url
        .replace('{api-version}', config.VERSION)
        .replace(/{(.*?)}/g, (substring: string, group: string) => {
            if (options.path?.hasOwnProperty(group)) {
                return encoder(String(options.path[group]));
            }
            return substring;
        });

    const url = `${config.BASE}${path}`;
    if (options.query) {
        return `${url}${getQueryString(options.query)}`;
    }
    return url;
};

const getFormData = (options: ApiRequestOptions): FormData | undefined => {
    if (options.formData) {
        const formData = new FormData();

        const process = (key: string, value: unknown) => {
            if (isString(value) || isBlob(value)) {
                formData.append(key, value);
            } else {
                formData.append(key, JSON.stringify(value));
            }
        };

        Object.entries(options.formData)
            .filter(([_, value]) => isDefined(value))
            .forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(v => process(key, v));
                } else {
                    process(key, value);
                }
            });

        return formData;
    }
    return undefined;
};

type Resolver<T> = (options: ApiRequestOptions) => Promise<T>;

const resolve = async <T>(options: ApiRequestOptions, resolver?: T | Resolver<T>): Promise<T | undefined> => {
    if (typeof resolver === 'function') {
        return (resolver as Resolver<T>)(options);
    }
    return resolver;
};

const getHeaders = async (config: OpenAPIConfig, options: ApiRequestOptions): Promise<Record<string, string>> => {
    // A live AMS session (in-memory access token) supersedes the legacy cookie;
    // the two artifacts are mutually exclusive by construction.
    const token = getToken() ?? getAuthCookie()?.id_token
    const username = await resolve(options, config.USERNAME);
    const password = await resolve(options, config.PASSWORD);
    const additionalHeaders = await resolve(options, config.HEADERS);

    const headers = Object.entries({
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
        ...additionalHeaders,
        ...options.headers,
    })
        .filter(([_, value]) => isDefined(value))
        .reduce((headers, [key, value]) => ({
            ...headers,
            [key]: String(value),
        }), {} as Record<string, string>);

    if (isStringWithValue(username) && isStringWithValue(password)) {
        const credentials = base64(`${username}:${password}`);
        headers['Authorization'] = `Basic ${credentials}`;
    }

    if (options.body) {
        if (options.mediaType) {
            headers['Content-Type'] = options.mediaType;
        } else if (isBlob(options.body)) {
            headers['Content-Type'] = options.body.type || 'application/octet-stream';
        } else if (isString(options.body)) {
            headers['Content-Type'] = 'text/plain';
        } else if (!isFormData(options.body)) {
            headers['Content-Type'] = 'application/json';
        }
    }

    return headers;
};

const getRequestBody = (options: ApiRequestOptions) => {
    if (options.body !== undefined) {
        if (options.mediaType?.includes('/json')) {
            return JSON.stringify(options.body)
        } else if (isString(options.body) || isBlob(options.body) || isFormData(options.body)) {
            return options.body;
        } else {
            return JSON.stringify(options.body);
        }
    }
    return undefined;
};

const sendRequest = async (
    config: OpenAPIConfig,
    options: ApiRequestOptions,
    url: string,
    body: BodyInit | null | undefined,
    formData: FormData | undefined,
    headers: Record<string, string>,
    onCancel: OnCancel,
): Promise<Response> => {
    const controller = new AbortController();

    const request: RequestInit = {
        headers,
        body: body ?? formData,
        method: options.method,
        signal: controller.signal,
    };

    if (config.WITH_CREDENTIALS) {
        request.credentials = config.CREDENTIALS;
    }

    onCancel(() => controller.abort());

    return await fetchWithRenewal(url, request);
};

const getResponseHeader = (response: Response, responseHeader?: string): string | undefined => {
    if (responseHeader) {
        const content = response.headers.get(responseHeader);
        if (isString(content)) {
            return content;
        }
    }
    return undefined;
};

const getResponseBody = async (response: Response) => {
    if (response.status !== 204) {
        try {
            const contentType = response.headers.get('Content-Type');
            if (contentType && BLOB_CONTENT_TYPES.includes(contentType)) {
                return response.blob();
            }
            if (contentType) {
                const jsonTypes = ['application/json', 'application/problem+json']
                const isJSON = jsonTypes.some(type => contentType.toLowerCase().startsWith(type));
                if (isJSON) {
                    return await response.json();
                } else {
                    return await response.text();
                }
            }
        } catch (error) {
            console.error(error);
        }
    }
    return undefined;
};

const catchErrorCodes = (options: ApiRequestOptions, result: ApiResult): void => {
    if (!result.ok) {
        captureApiError({ method: options.method, url: result.url, status: result.status });
    }

    const errors: Record<number, string> = {
        400: 'Bad Request',
        401: 'Unauthorized',
        403: 'Forbidden',
        404: 'Not Found',
        408: 'Request Timeout',
        409: 'Conflict',
        429: 'Too Many Requests',
        500: 'Internal Server Error',
        502: 'Bad Gateway',
        503: 'Service Unavailable',
        // Gateway timeouts carry no response body, so this string is all the user sees. It has to say
        // more than "Gateway Timeout": the server keeps working after the proxy gives up, and blind
        // retries pile up on row locks behind the request that is still running.
        504: 'The server took too long to respond. The operation may still be finishing - reload the page before trying again.',
        ...options.errors,
    }

    if (result.status === 401) {
        // Legacy sessions die by logout redirect. On the AMS stack the 401
        // already went through renew-then-retry; the surfaced error belongs to
        // the caller while session death reaches the gate as a published outcome.
        if (typeof window !== 'undefined' && !isAmsAuthActive()) {
            logout();
      }
    }

    const error = errors[result.status];

    if (error) {
        throw new ApiError(options, result, result.body?.error || result.body?.message || error);
    }

    if (!result.ok) {
        throw new ApiError(options, result, 'Generic Error');
    }
};

/**
 * Request method
 * @param config The OpenAPI configuration object
 * @param options The request options from the service
 * @returns CancelablePromise<T>
 * @throws ApiError
 */
export const request = <T>(config: OpenAPIConfig, options: ApiRequestOptions): CancelablePromise<T> => {
  return new CancelablePromise<T>(
    async (resolve: (value: T | PromiseLike<T>) => void,
    reject: (reason?: unknown) => void,
    onCancel: OnCancel
  ) => {
    try {
      const url = getUrl(config, options);
      const formData = getFormData(options);
      const body = getRequestBody(options);
      const headers = await getHeaders(config, options);

      if (!onCancel.isCancelled) {
        const response = await sendRequest(config, options, url, body, formData, headers, onCancel);
        const responseBody = await getResponseBody(response);
        const responseHeader = getResponseHeader(response, options.responseHeader);

        const result: ApiResult = {
          url,
          ok: response.ok,
          status: response.status,
          statusText: response.statusText,
          body: responseHeader ?? responseBody,
        };

        catchErrorCodes(options, result);

        resolve(result.body);
      }
    } catch (error) {
      reject(error);
    }
  });
};
